import { useTranslation } from 'react-i18next';
import {
  SEGMENT_PREFIXES,
  formatJSON,
  isFiniteNumber,
  objectItems,
  textOf,
  translateSegment,
} from './aiClipSafeRender';

/**
 * AI剪辑记录（katabit-edit-record/v1）的语义化渲染：
 * 摘要胶囊 + 逐镜卡片 + 审计折叠区；schema 不匹配时回退通用 JSON 折叠树。
 * 结构契约见 KataBit backend/internal/render/record.go。
 *
 * record.json 与 warnings.json 同源于 KataBit（外部剪辑代理 + 人工评论），
 * 字段类型不受本仓约束：数组元素一律经 objectItems 过滤、进 JSX 的标量一律经
 * textOf 收窄、动态 i18n key 一律经 translateSegment 白名单化（见 aiClipSafeRender）。
 */

interface RecordSegment {
  source?: string;
  startSeconds?: number;
  endSeconds?: number;
}

interface RecordShot {
  shotIndex?: number;
  status?: string;
  selection?: { mode?: string; versionIds?: string[] };
  segments?: RecordSegment[];
  decisionBasis?: { summary?: string };
  reason?: string;
}

interface CommentResolution {
  commentKey?: string;
  status?: string;
  reason?: string;
}

interface OverlapDecision {
  id?: string;
  reason?: string;
}

interface BoundaryDecision {
  boundaryId?: string;
  status?: string;
  resolvedSeconds?: number;
  reason?: string;
}

interface GateAcknowledgement {
  gate?: string;
  startSeconds?: number;
  endSeconds?: number;
  reason?: string;
}

interface EditRecordV1 {
  schemaVersion: string;
  shots: RecordShot[];
  editorialAudit?: {
    commentResolutions?: CommentResolution[];
    overlapDecisions?: OverlapDecision[];
  };
  frameBoundaryAudit?: {
    decisions?: BoundaryDecision[];
  };
  gateAcknowledgements?: GateAcknowledgement[];
  outputSpec?: { width?: number; height?: number; frameRate?: number };
  outputs?: { durationSeconds?: number; frameCount?: number };
}

function isEditRecordV1(record: unknown): record is EditRecordV1 {
  // 数组也满足 typeof === 'object'；与 isWarningsV1 保持同一口径，数组一律回退通用视图
  if (typeof record !== 'object' || record === null || Array.isArray(record)) return false;
  const r = record as Record<string, unknown>;
  return typeof r.schemaVersion === 'string'
    && r.schemaVersion.startsWith('katabit-edit-record/')
    && Array.isArray(r.shots);
}

const SHOT_STATUS_STYLES: Record<string, { color: string; bg: string }> = {
  used: { color: 'var(--success, #1a7f37)', bg: 'rgba(26, 127, 55, 0.1)' },
  skipped: { color: 'var(--text-3, #999)', bg: 'rgba(128, 128, 128, 0.12)' },
  needs_clarification: { color: 'var(--warning, #b45309)', bg: 'rgba(180, 83, 9, 0.12)' },
};

export const pillStyle = (color: string, bg: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 8px',
  borderRadius: '999px',
  fontSize: '0.72rem',
  fontWeight: 500,
  color,
  background: bg,
  whiteSpace: 'nowrap',
});

export const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  padding: '1px 6px',
  borderRadius: '4px',
  fontSize: '0.72rem',
  fontFamily: 'ui-monospace, SFMono-Regular, monospace',
  color: 'var(--text-2)',
  background: 'var(--bg, rgba(128,128,128,0.08))',
  border: '1px solid var(--border)',
};

// overflowWrap: 右栏只占 2/5 宽，缺失台词等外部长文本可能不含空格，需强制断行避免横向滚动
export const mutedStyle: React.CSSProperties = {
  fontSize: '0.76rem',
  color: 'var(--text-3)',
  lineHeight: 1.5,
  overflowWrap: 'anywhere',
};

// NaN / Infinity 也满足 typeof === 'number'，不查有限性会渲染出 "NaNs"
export const formatSeconds = (v?: unknown): string => (isFiniteNumber(v) ? `${v.toFixed(1)}s` : '?');

/** 序列化失败（深嵌套 / 循环引用）时的占位文案，两个视图共用。 */
export const useUnserializableLabel = (): string => {
  const { t } = useTranslation('episodeDetail');
  return t('aiClip.record.unserializable', { defaultValue: '（无法序列化的负载）' });
};

/** 回退：顶层逐条折叠的通用 JSON 树。 */
export function GenericJSONView({ record }: { record: unknown }) {
  const unserializable = useUnserializableLabel();
  if (typeof record === 'object' && record !== null) {
    return (
      <>
        {Object.entries(record as Record<string, unknown>).map(([key, value]) => (
          <details key={key}>
            <summary style={{ cursor: 'pointer' }}>{key}</summary>
            <pre style={{ fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {formatJSON(value, unserializable)}
            </pre>
          </details>
        ))}
      </>
    );
  }
  return (
    <details>
      <summary style={{ cursor: 'pointer' }}>{typeof record}</summary>
      <pre style={{ fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {formatJSON(record, unserializable)}
      </pre>
    </details>
  );
}

function ShotCard({ shot }: { shot: RecordShot }) {
  const { t } = useTranslation('episodeDetail');
  const status = textOf(shot.status);
  // 白名单 SHOT_STATUS_STYLES 只按已知 status 取样式，原型链名字不会命中（自有属性查找由 hasOwnProperty 保证）
  const statusStyle = (Object.prototype.hasOwnProperty.call(SHOT_STATUS_STYLES, status)
    && SHOT_STATUS_STYLES[status]) || SHOT_STATUS_STYLES.skipped;
  const statusLabel = translateSegment(t, SEGMENT_PREFIXES.shotStatus, status);
  const mode = textOf(shot.selection?.mode);
  const modeLabel = translateSegment(t, SEGMENT_PREFIXES.selectionMode, mode);
  const versionIds = shot.selection?.versionIds;
  const versionCount = Array.isArray(versionIds) ? versionIds.length : 0;
  const segments = objectItems<RecordSegment>(shot.segments);
  // 契约是 number；textOf 会放行任意字符串，而 shotIndex 同时进了 i18next 的插值变量
  // 与 defaultValue，后者仍跑 $t() 嵌套 —— 一旦 shotLabel 这个 key 被改名/删除，
  // 外部字符串就能伪装成本产品文案。这里直接收窄成「有限数字或 ?」。
  const shotIndex = isFiniteNumber(shot.shotIndex) ? String(shot.shotIndex) : '?';
  const note = textOf(shot.reason) || textOf(shot.decisionBasis?.summary);

  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '0.5rem 0.65rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.3rem',
        background: 'var(--surface)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <strong style={{ fontSize: '0.8rem' }}>
          {t('aiClip.record.shotLabel', { index: shotIndex, defaultValue: `镜头 ${shotIndex}` })}
        </strong>
        <span style={pillStyle(statusStyle.color, statusStyle.bg)}>{statusLabel}</span>
        <span style={{ flex: 1 }} />
        {mode && (
          <span style={pillStyle('var(--info, #0075de)', 'rgba(0, 117, 222, 0.1)')}>
            {modeLabel}
            {versionCount > 1 ? ` ×${versionCount}` : ''}
          </span>
        )}
      </div>
      {segments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
          {segments.map((seg, i) => (
            <span key={i} style={chipStyle}>
              {formatSeconds(seg.startSeconds)}–{formatSeconds(seg.endSeconds)}
            </span>
          ))}
        </div>
      )}
      {note && <div style={mutedStyle}>{note}</div>}
    </div>
  );
}

function AuditSection({ record }: { record: EditRecordV1 }) {
  const { t } = useTranslation('episodeDetail');
  const comments = objectItems<CommentResolution>(record.editorialAudit?.commentResolutions);
  const overlaps = objectItems<OverlapDecision>(record.editorialAudit?.overlapDecisions);
  const boundaries = objectItems<BoundaryDecision>(record.frameBoundaryAudit?.decisions);
  const gates = objectItems<GateAcknowledgement>(record.gateAcknowledgements);
  const total = comments.length + overlaps.length + boundaries.length + gates.length;
  if (total === 0) return null;

  const itemStyle: React.CSSProperties = {
    ...mutedStyle,
    padding: '0.25rem 0',
    borderBottom: '1px dashed var(--border)',
  };

  return (
    <details style={{ marginTop: '0.25rem' }}>
      <summary style={{ cursor: 'pointer', fontSize: '0.8rem', color: 'var(--text-2)' }}>
        {t('aiClip.record.auditTitle', { count: total, defaultValue: `剪辑审计（${total}）` })}
      </summary>
      <div style={{ paddingLeft: '0.25rem' }}>
        {comments.map((c, i) => {
          const reason = textOf(c.reason);
          // status 缺失或非标量时 translateSegment 返回空串；无条件渲染会留下一颗空胶囊
          // （与 AIClipWarningsView 的评论状态口径不一致），所以整颗胶囊连同后缀空格一起省掉
          const statusLabel = translateSegment(t, SEGMENT_PREFIXES.recordCommentStatus, c.status);
          return (
            <div key={`c-${i}`} style={itemStyle}>
              {statusLabel && (
                <span style={pillStyle('var(--text-2)', 'rgba(128,128,128,0.1)')}>
                  {statusLabel}
                </span>
              )}
              {statusLabel ? ' ' : ''}{textOf(c.commentKey)}{reason ? ` — ${reason}` : ''}
            </div>
          );
        })}
        {overlaps.map((o, i) => {
          const reason = textOf(o.reason);
          return (
            <div key={`o-${i}`} style={itemStyle}>
              {t('aiClip.record.overlap', { defaultValue: '重叠决策' })} {textOf(o.id)}
              {reason ? ` — ${reason}` : ''}
            </div>
          );
        })}
        {boundaries.map((b, i) => {
          const reason = textOf(b.reason);
          return (
            <div key={`b-${i}`} style={itemStyle}>
              {t('aiClip.record.frameBoundary', { defaultValue: '帧边界' })} {textOf(b.boundaryId)}
              {isFiniteNumber(b.resolvedSeconds) ? ` @ ${b.resolvedSeconds.toFixed(2)}s` : ''}
              {reason ? ` — ${reason}` : ''}
            </div>
          );
        })}
        {gates.map((g, i) => {
          const reason = textOf(g.reason);
          return (
            <div key={`g-${i}`} style={itemStyle}>
              {t('aiClip.record.gate', { defaultValue: '门禁确认' })} {textOf(g.gate)}
              {' '}{formatSeconds(g.startSeconds)}–{formatSeconds(g.endSeconds)}
              {reason ? ` — ${reason}` : ''}
            </div>
          );
        })}
      </div>
    </details>
  );
}

export default function AIClipRecordView({ record }: { record: unknown }) {
  const { t } = useTranslation('episodeDetail');
  const unserializable = useUnserializableLabel();

  if (!isEditRecordV1(record)) {
    return <GenericJSONView record={record} />;
  }

  const shots = objectItems<RecordShot>(record.shots);
  const usedCount = shots.filter(s => s.status === 'used').length;
  const spec = record.outputSpec;
  const durationSeconds = record.outputs?.durationSeconds;
  const frameCount = record.outputs?.frameCount;
  // 三元保留原有的「0 / 缺失即不渲染」语义，textOf 负责把非标量收窄成空串
  const specWidth = spec?.width ? textOf(spec.width) : '';
  const specHeight = spec?.height ? textOf(spec.height) : '';
  const frameRate = spec?.frameRate ? textOf(spec.frameRate) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
        {isFiniteNumber(durationSeconds) && (
          <span style={chipStyle}>{t('aiClip.record.duration', { defaultValue: '成片' })} {durationSeconds.toFixed(1)}s</span>
        )}
        {isFiniteNumber(frameCount) && (
          <span style={chipStyle}>{frameCount} {t('aiClip.record.frames', { defaultValue: '帧' })}</span>
        )}
        {specWidth && specHeight && (
          <span style={chipStyle}>
            {specWidth}×{specHeight}{frameRate ? ` @ ${frameRate}fps` : ''}
          </span>
        )}
        <span style={chipStyle}>
          {t('aiClip.record.shotsUsed', { used: usedCount, total: shots.length, defaultValue: `镜头 ${usedCount}/${shots.length} 保留` })}
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {/* key 用下标：不可信 shotIndex 可能重复或不是标量，做 key 会撞 key/降级成 "[object Object]" */}
        {shots.map((shot, i) => (
          <ShotCard key={i} shot={shot} />
        ))}
      </div>

      <AuditSection record={record} />

      <details style={{ marginTop: '0.25rem' }}>
        <summary style={{ cursor: 'pointer', fontSize: '0.78rem', color: 'var(--text-3)' }}>
          {t('aiClip.record.rawJson', { defaultValue: '查看原始 JSON' })}
        </summary>
        <pre style={{ fontSize: '0.74rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
          {formatJSON(record, unserializable)}
        </pre>
      </details>
    </div>
  );
}
